import React from 'react';
import styled from 'styled-components';

type OrderImageGridProps = {
  images: string[];
};

const MAX_VISIBLE = 5;
const MAX_CELLS = 6;
const COLS = 3;

function getGridItems(images: string[]): { visibleImages: string[]; moreCount: number } {
  if (images.length <= MAX_CELLS) {
    return { visibleImages: images, moreCount: 0 };
  }
  return { visibleImages: images.slice(0, MAX_VISIBLE), moreCount: images.length - MAX_VISIBLE };
}

export function OrderImageGrid({ images }: OrderImageGridProps): JSX.Element {
  const { visibleImages, moreCount } = getGridItems(images);
  const totalCells = moreCount > 0 ? MAX_CELLS : visibleImages.length;
  const rows = Math.ceil(totalCells / COLS);
  const lastRowCount = totalCells - (rows - 1) * COLS;

  return (
    <Grid data-testid='order-image-grid'>
      {Array.from({ length: rows }, (_, rowIndex) => {
        const isLastRow = rowIndex === rows - 1;
        const cellsInRow = isLastRow ? lastRowCount : COLS;
        const startIndex = rowIndex * COLS;

        return (
          <GridRow key={rowIndex} $centered={isLastRow && cellsInRow < COLS}>
            {Array.from({ length: cellsInRow }, (_col, colIndex) => {
              const imageIndex = startIndex + colIndex;
              const isMoreBadge = moreCount > 0 && imageIndex === MAX_VISIBLE;

              if (isMoreBadge) {
                return (
                  <MoreBadge key='more' $cellsInRow={cellsInRow} data-testid='order-card-more-badge'>
                    <span>+{moreCount}</span>
                    <span>more</span>
                  </MoreBadge>
                );
              }

              return (
                <ImageCell key={visibleImages[imageIndex]} $cellsInRow={cellsInRow} $isSingle={totalCells === 1}>
                  <img src={visibleImages[imageIndex]} alt='Product thumbnail' />
                </ImageCell>
              );
            })}
          </GridRow>
        );
      })}
    </Grid>
  );
}

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  aspect-ratio: 232 / 152;
  justify-content: center;
`;

const GridRow = styled.div<{ $centered: boolean }>`
  display: flex;
  gap: 8px;
  justify-content: ${({ $centered }) => ($centered ? 'center' : 'flex-start')};
`;

function cellFlexBasis(cellsInRow: number): string {
  if (cellsInRow === 1) return '100%';
  const gapTotal = (cellsInRow - 1) * 8;
  return `calc((100% - ${gapTotal}px) / ${cellsInRow})`;
}

const ImageCell = styled.div<{ $cellsInRow: number; $isSingle: boolean }>`
  flex: 0 0 ${({ $cellsInRow }) => cellFlexBasis($cellsInRow)};
  aspect-ratio: ${({ $isSingle }) => ($isSingle ? 'auto' : '1')};
  border-radius: 4px;
  overflow: hidden;
  ${({ $isSingle }) => $isSingle && 'flex: 1;'}

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const MoreBadge = styled.div<{ $cellsInRow: number }>`
  flex: 0 0 ${({ $cellsInRow }) => cellFlexBasis($cellsInRow)};
  aspect-ratio: 1;
  background-color: ${({ theme }) => theme.colors.grey[95]};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.grey[40]};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 1;
`;
